import React, { forwardRef, useId } from 'react';
import type { CheckboxProps } from './Checkbox.types';

import { cn } from "../../lib/utils";

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      id: providedId,
      className = '',
      label,
      description,
      fullWidth = false,
      size = 'md',
      error = false,
      invalid = false,
      labelPosition = 'end',
      errorMessage,
      disabled,
      indeterminate,
      ...props
    },
    ref
  ) => {
    const fallbackId = useId();
    const id = providedId ?? fallbackId;

    // Configurar el ref y el estado indeterminate cuando se monta el input
    const inputRef = React.useRef<HTMLInputElement>(null);
    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    React.useEffect(() => {
      if (inputRef.current) {
        inputRef.current.indeterminate = indeterminate ?? false;
      }
    }, [indeterminate]);

    const isError = error || invalid;

    const sizeClasses = {
      sm: 'w-4 h-4',
      md: 'w-5 h-5',
      lg: 'w-6 h-6',
    };

    const textSizes = {
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
    };

    const containerClasses = [
      'flex items-start gap-3',
      labelPosition === 'start' ? 'flex-row-reverse justify-end w-full' : 'flex-row',
      fullWidth ? 'w-full' : 'w-auto',
      disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
      className,
    ].join(' ');

    // La caja visible es un <span> hermano estilizado con `peer-*` a partir
    // del estado del <input> (que va oculto pero accesible). Al ser un
    // elemento normal (no reemplazado) admite hijos y no lo tocan los resets
    // de `input[type=checkbox]` de las apps consumidoras. El relleno usa `!`
    // para ganar la cascada frente a esos resets.
    const boxClasses = [
      'pointer-events-none relative shrink-0 box-border',
      'flex items-center justify-center',
      'rounded border transition-colors duration-200',
      'bg-white dark:bg-gray-800',
      sizeClasses[size],
      isError
        ? 'border-destructive dark:border-destructive shadow-sm'
        : 'border-zinc-200 dark:border-zinc-600',
      // Relleno azul cuando está marcado o indeterminado
      'peer-checked:bg-primary! peer-checked:border-primary-500! dark:peer-checked:bg-primary-200! dark:peer-checked:border-primary-300!',
      'peer-indeterminate:bg-primary! peer-indeterminate:border-primary-500! dark:peer-indeterminate:bg-primary-200! dark:peer-indeterminate:border-primary-300!',
      isError
        ? 'peer-checked:border-destructive! peer-indeterminate:border-destructive!'
        : '',
      // Anillo de foco por teclado (se dispara desde el input via peer)
      isError
        ? 'peer-focus-visible:outline-none peer-focus-visible:ring-4 peer-focus-visible:ring-red-400 peer-focus-visible:ring-offset-2 dark:peer-focus-visible:ring-red-500/50 peer-focus-visible:ring-offset-red-100 dark:peer-focus-visible:ring-offset-gray-900'
        : 'peer-focus-visible:outline-none peer-focus-visible:ring-4 peer-focus-visible:ring-primary-400 peer-focus-visible:ring-offset-2 dark:peer-focus-visible:ring-primary-500/50 peer-focus-visible:ring-offset-primary-100 dark:peer-focus-visible:ring-offset-gray-900',
    ].join(' ');

    return (
      <div className={containerClasses}>
        <div className={[
          "flex items-center h-full pt-0.5",
          labelPosition === 'start' ? "ml-auto" : ""
        ].join(' ')}>
          <label htmlFor={id} className="relative inline-flex cursor-inherit">
            <input
              type="checkbox"
              id={id}
              ref={inputRef}
              className="peer sr-only"
              disabled={disabled}
              aria-invalid={isError}
              aria-describedby={isError && errorMessage ? `${id}-error` : description ? `${id}-description` : undefined}
              {...props}
            />
            {/* Caja visible */}
            <span className={boxClasses} aria-hidden="true" />
            {/* Check: visible sólo cuando checked y NO indeterminado.
                Debe ser hermano del <input> para que `peer-checked` aplique. */}
            <svg
              aria-hidden="true"
              viewBox="0 0 16 16"
              fill="none"
              className="pointer-events-none absolute inset-0 m-auto w-[75%] h-[75%] opacity-0 text-white dark:text-primary-700 peer-checked:opacity-100 peer-indeterminate:opacity-0"
            >
              <path
                d="M13 4.5 6.5 11.5 3 8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {/* Guion: visible en estado indeterminado */}
            <svg
              aria-hidden="true"
              viewBox="0 0 16 16"
              fill="none"
              className="pointer-events-none absolute inset-0 m-auto w-[70%] h-[70%] opacity-0 text-white dark:text-primary-700 peer-indeterminate:opacity-100"
            >
              <path
                d="M3.5 8h9"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </label>
        </div>

        {(label || description || (isError && errorMessage)) && (
          <div className="flex flex-col gap-1 flex-1">
            {label && (
              <label
                htmlFor={id}
                className={cn(
                  'font-medium text-zinc-900 dark:text-zinc-100 cursor-inherit select-none',
                  isError && 'text-destructive dark:text-destructive',
                  textSizes[size]
                )}
              >
                {label}
              </label>
            )}

            {description && (
              <p
                id={`${id}-description`}
                className={`text-sm select-none ${isError ? 'text-destructive dark:text-destructive' : 'text-zinc-400'}`}
              >
                {description}
              </p>
            )}

            {isError && errorMessage && (
              <p
                id={`${id}-error`}
                className="text-sm text-destructive dark:text-destructive select-none"
              >
                {errorMessage}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';